import React, { useState, useRef } from "react";
import apiClient from "@/api/client";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Upload, FileSpreadsheet, AlertCircle, CheckCircle2 } from "lucide-react";

const fieldOptions = [
  { value: "last_name", label: "Nom" },
  { value: "first_name", label: "Prénom" },
  { value: "email", label: "Email" },
  { value: "phone", label: "Téléphone" },
  { value: "subscription", label: "Abonnement" },
  { value: "start_date", label: "Date début" },
  { value: "_skip", label: "Ignorer" },
];

const ImportPage = () => {
  const { isAdmin } = useAuth();
  const [step, setStep] = useState("upload"); // upload | mapping | result
  const [preview, setPreview] = useState(null);
  const [mapping, setMapping] = useState({});
  const [allRows, setAllRows] = useState([]);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const fileRef = useRef(null);

  // Guard for non-admin users
  if (!isAdmin) {
    return (
      <div className="crm-page">
        <p className="text-[#577083]">Accès réservé à l'administrateur</p>
      </div>
    );
  }

  const handleUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setLoading(true);
    const formData = new FormData();
    formData.append("file", file);
    try {
      const res = await apiClient.post("/import/preview", formData, { headers: { "Content-Type": "multipart/form-data" } });
      setPreview(res.data);
      setAllRows(res.data.rows);
      // Auto-map columns
      const autoMap = {};
      for (const col of res.data.columns) {
        const cl = col.toLowerCase();
        if (cl.includes("nom") && !cl.includes("pré") && !cl.includes("pre")) autoMap.last_name = col;
        else if (cl.includes("prénom") || cl.includes("prenom") || (cl.includes("pré") && cl.includes("nom"))) autoMap.first_name = col;
        else if (cl.includes("mail")) autoMap.email = col;
        else if (cl.includes("tél") || cl.includes("tel") || cl.includes("phone")) autoMap.phone = col;
        else if (cl.includes("abon") || cl.includes("plan") || cl.includes("type")) autoMap.subscription = col;
        else if (cl.includes("date") && (cl.includes("début") || cl.includes("debut") || cl.includes("effet") || cl.includes("inscription"))) autoMap.start_date = col;
      }
      setMapping(autoMap);
      setStep("mapping");
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleImport = async () => {
    setLoading(true);
    try {
      // Invert mapping: field -> col becomes col -> field for backend
      const invertedMapping = {};
      Object.entries(mapping).forEach(([field, col]) => {
        if (field !== "_skip") invertedMapping[field] = col;
      });
      const res = await apiClient.post("/import/execute", { rows: allRows, mapping: invertedMapping });
      setResult(res.data);
      setStep("result");
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const updateMapping = (field, col) => {
    setMapping((prev) => ({ ...prev, [field]: col }));
  };

  return (
    <div className="crm-page">
      <div className="crm-page-header">
        <div>
          <h1 className="crm-page-title">Import CSV</h1>
          <p className="crm-page-subtitle">Importer des membres depuis un fichier CSV</p>
        </div>
      </div>

      {step === "upload" && (
        <div className="crm-card">
          <div className="flex flex-col items-center py-12">
            <FileSpreadsheet size={48} className="text-[#3899ec] mb-4" />
            <p className="text-[#577083] mb-6 text-center">Sélectionnez un fichier CSV avec les colonnes : Nom, Prénom, Abonnement, Date début...</p>
            <input type="file" ref={fileRef} accept=".csv" onChange={handleUpload} className="hidden" />
            <Button onClick={() => fileRef.current?.click()} disabled={loading} className="bg-[#1a2744] hover:bg-[#243459] text-[#e8d44d]">
              <Upload size={16} className="mr-2" />
              {loading ? "Analyse en cours..." : "Charger un fichier CSV"}
            </Button>
          </div>
        </div>
      )}

      {step === "mapping" && preview && (
        <>
          <div className="crm-card mb-4">
            <h3 className="crm-card-title">Mapping des colonnes</h3>
            <p className="text-sm text-[#577083] mb-4">{preview.total} lignes détectées. Associez chaque colonne au bon champ.</p>
            <div className="grid grid-cols-2 gap-3 max-w-lg">
              {fieldOptions.filter(f => f.value !== "_skip").map((field) => (
                <div key={field.value} className="flex flex-col gap-1">
                  <label className="text-xs font-medium text-[#32536a]">{field.label}</label>
                  <Select value={mapping[field.value] || ""} onValueChange={(v) => updateMapping(field.value, v)}>
                    <SelectTrigger className="h-9 text-sm"><SelectValue placeholder="Colonne..." /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="_none">Non mappé</SelectItem>
                      {preview.columns.map((col) => <SelectItem key={col} value={col}>{col}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              ))}
            </div>
          </div>

          <div className="crm-card mb-4">
            <h3 className="crm-card-title">Aperçu ({Math.min(preview.rows.length, 10)} premières lignes)</h3>
            <div className="crm-table-wrap" style={{boxShadow:'none'}}>
              <table className="crm-table">
                <thead><tr>{preview.columns.map((c) => <th key={c}>{c}</th>)}<th>Doublon</th></tr></thead>
                <tbody>
                  {preview.rows.slice(0, 10).map((row, i) => (
                    <tr key={i} className={row._duplicate ? "crm-row-yellow" : ""}>
                      {preview.columns.map((c) => <td key={c}>{row[c]}</td>)}
                      <td>{row._duplicate ? <AlertCircle size={14} className="text-yellow-600" /> : ""}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="flex gap-3">
            <Button variant="outline" onClick={() => { setStep("upload"); setPreview(null); }}>Retour</Button>
            <Button onClick={handleImport} disabled={loading || !mapping.last_name || !mapping.first_name} className="bg-[#1a2744] hover:bg-[#243459] text-[#e8d44d]">
              {loading ? "Import en cours..." : `Importer ${preview.total} lignes`}
            </Button>
          </div>
        </>
      )}

      {step === "result" && result && (
        <div className="crm-card">
          <div className="flex flex-col items-center py-8">
            <CheckCircle2 size={48} className="text-green-500 mb-4" />
            <h3 className="text-lg font-bold text-[#162d3d] mb-4">Import terminé !</h3>
            <div className="flex gap-6 text-center">
              <div><span className="text-2xl font-bold text-green-600">{result.created}</span><br /><span className="text-sm text-[#577083]">Créés</span></div>
              <div><span className="text-2xl font-bold text-yellow-600">{result.skipped}</span><br /><span className="text-sm text-[#577083]">Doublons ignorés</span></div>
              <div><span className="text-2xl font-bold text-red-500">{result.incomplete}</span><br /><span className="text-sm text-[#577083]">Incomplets</span></div>
            </div>
            <Button onClick={() => { setStep("upload"); setPreview(null); setResult(null); }} className="mt-6 bg-[#1a2744] hover:bg-[#243459] text-[#e8d44d]">
              Nouvel import
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ImportPage;
